import { useEffect, useState, useCallback, useMemo } from "react";
import { getRecycleBin, restoreRecycleBinRecord } from "../../services/recycleBin_service";

import Header from "../../components/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";

import "./RecycleBin.css";

const TABS = [
  { key: "drivers",              label: "Drivers" },
  { key: "vehicles",              label: "Vehicles" },
  { key: "suppliers",             label: "Suppliers" },
  { key: "vehicle_assignments",   label: "Allotments" },
  { key: "users",                 label: "Staff" },
  { key: "vehicle_documents",     label: "Vehicle Documents" },
];

const TITLE_FIELD = {
  drivers:              "full_name",
  vehicles:              "vehicle_registration_number",
  suppliers:             "supplier_name",
  vehicle_assignments:   "unique_number",
  users:                 "full_name",
  vehicle_documents:     "display_code",
};

const SUBTITLE_FIELD = {
  drivers:              "driver_code",
  vehicles:              "vehicle_code",
  suppliers:             "supplier_code",
  vehicle_assignments:   "transaction_id",
  users:                 "staff_code",
  vehicle_documents:     "rc_file_url",
};

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
function groupByDate(records) {
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const dayBefore = new Date(today);
  dayBefore.setDate(today.getDate() - 2);

  const labelFor = (value) => {
    if (!value) return "Earlier";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "Earlier";
    const day = startOfDay(d);
    if (day.getTime() === today.getTime()) return "Today";
    if (day.getTime() === yesterday.getTime()) return "Yesterday";
    if (day.getTime() === dayBefore.getTime()) return "Day Before Yesterday";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const order = [];
  const buckets = new Map();

  for (const record of records) {
    const label = labelFor(record.deleted_at);
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label).push(record);
  }

  return order.map((label) => ({ label, items: buckets.get(label) }));
}

export default function RecycleBin() {
  const [activeTab, setActiveTab]   = useState(TABS[0].key);
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [toast, setToast]           = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const loadRecords = useCallback(async (table) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRecycleBin(table);
      setRecords(data || []);
    } catch (err) {
      console.error("Failed to load recycle bin:", err);
      setError(
        err?.response?.status === 404
          ? "This section isn't available yet."
          : "Couldn't load deleted records. Please try again."
      );
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords(activeTab);
  }, [activeTab, loadRecords]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleRestoreClick = (record) => {
    setConfirmTarget(record);
  };

  const handleConfirmRestore = async () => {
    if (!confirmTarget) return;
    const record = confirmTarget;
    setConfirmTarget(null);
    setRestoringId(record.id);

    try {
      await restoreRecycleBinRecord(activeTab, record.id);
      setRecords((prev) => prev.filter((r) => r.id !== record.id));
      setToast({ type: "success", text: "Record restored successfully." });
    } catch (err) {
      console.error("Restore failed:", err);
      setToast({
        type: "error",
        text: err?.response?.data?.detail || "Couldn't restore this record. Please try again.",
      });
    } finally {
      setRestoringId(null);
    }
  };

  const titleField    = TITLE_FIELD[activeTab]    || "id";
  const subtitleField = SUBTITLE_FIELD[activeTab] || null;

  const groups = useMemo(() => groupByDate(records), [records]);

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />
        <div className="recycle-bin-page">

          <div className="recycle-bin-tabs" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`recycle-bin-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="recycle-bin-body">
            {loading && (
              <div className="recycle-bin-state">
                <div className="spinner" />
                <span>Loading deleted records…</span>
              </div>
            )}

            {!loading && error && (
              <div className="recycle-bin-state recycle-bin-state-error">
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && records.length === 0 && (
              <div className="recycle-bin-state recycle-bin-empty">
                <span>Nothing here. Deleted {TABS.find(t => t.key === activeTab)?.label.toLowerCase()} will show up in this list.</span>
              </div>
            )}

            {!loading && !error && records.length > 0 && (
              <div className="recycle-bin-groups">
                {groups.map((group) => (
                  <section className="recycle-bin-group" key={group.label}>
                    <div className="recycle-bin-group-header">
                      <span className="recycle-bin-group-label">{group.label}</span>
                      <span className="recycle-bin-group-count">{group.items.length}</span>
                      <span className="recycle-bin-group-line" />
                    </div>

                    <div className="recycle-bin-grid">
                      {group.items.map((record) => (
                        <div className="recycle-bin-card" key={record.id}>
                          <div className="recycle-bin-card-top">
                            <span className="recycle-bin-card-title">
                              {record[titleField] || `#${record.id}`}
                            </span>
                            {subtitleField && record[subtitleField] && (
                              <span className="recycle-bin-card-code">{record[subtitleField]}</span>
                            )}
                          </div>

                          <div className="recycle-bin-card-meta">
                            <span className="recycle-bin-card-deleted-by">
                              Deleted by <strong>{record.deleted_by || "Unknown"}</strong>
                            </span>
                            <span className="recycle-bin-card-time">{formatTime(record.deleted_at)}</span>
                          </div>

                          <button
                            type="button"
                            className="recycle-bin-restore-btn"
                            disabled={restoringId === record.id}
                            onClick={() => handleRestoreClick(record)}
                          >
                            {restoringId === record.id ? "Restoring…" : "Restore"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {confirmTarget && (
            <div className="recycle-bin-modal-backdrop" onClick={() => setConfirmTarget(null)}>
              <div className="recycle-bin-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Restore this record?</h3>
                <p>
                  {confirmTarget[titleField] || `Record #${confirmTarget.id}`} will be moved back
                  into the active list.
                </p>
                <div className="recycle-bin-modal-actions">
                  <button className="btn-secondary" onClick={() => setConfirmTarget(null)}>
                    Cancel
                  </button>
                  <button className="recycle-bin-restore-btn" onClick={handleConfirmRestore}>
                    Restore
                  </button>
                </div>
              </div>
            </div>
          )}

          {toast && (
            <div className={`recycle-bin-toast ${toast.type}`} role="status">
              {toast.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}